/**
 * Status Comment Prompt Store (SD-145)
 *
 * Statuses can require a comment. Any code path that moves tasks into such a
 * status (a picker, a board drop, a bulk action, or the API-layer fallback
 * after a STATUS_COMMENT_REQUIRED response) asks through this store; the one
 * mounted StatusCommentPromptHost renders the dialog and settles the promise.
 *
 * Resolves with the trimmed comment, or null when the person cancels.
 */

import { create } from 'zustand'

export interface StatusCommentPromptRequest {
  statusName: string
  statusColor?: string | null
  /** How many tasks the comment will be posted to (1 for a single move). */
  taskCount: number
}

interface PendingStatusCommentPrompt extends StatusCommentPromptRequest {
  id: number
  resolvers: ((comment: string | null) => void)[]
}

interface StatusCommentPromptState {
  pending: PendingStatusCommentPrompt | null
  /** Open the dialog; resolves with the comment, or null on cancel. */
  requestComment: (
    request: StatusCommentPromptRequest,
  ) => Promise<string | null>
  /** Settle the open prompt (the host calls this on submit or cancel). */
  settle: (comment: string | null) => void
}

let nextPromptId = 1

export const useStatusCommentPromptStore = create<StatusCommentPromptState>(
  (set, get) => ({
    pending: null,
    requestComment: request =>
      new Promise<string | null>(resolve => {
        const pending = get().pending
        if (pending) {
          // Concurrent requests (e.g. one bulk action split per status) share
          // the open prompt: one comment answers all of them.
          set({
            pending: {
              ...pending,
              taskCount: pending.taskCount + request.taskCount,
              resolvers: [...pending.resolvers, resolve],
            },
          })
          return
        }
        set({
          pending: { ...request, id: nextPromptId++, resolvers: [resolve] },
        })
      }),
    settle: comment => {
      const pending = get().pending
      if (!pending) return
      set({ pending: null })
      const trimmed = comment?.trim() ?? ''
      for (const resolve of pending.resolvers) resolve(trimmed ? trimmed : null)
    },
  }),
)

/** Non-hook entry point for API-layer code. */
export const requestStatusComment = (
  request: StatusCommentPromptRequest,
): Promise<string | null> =>
  useStatusCommentPromptStore.getState().requestComment(request)
